/**
 * SEO Component
 * Manages dynamic meta tags for different pages
 * Updates page title, description, and Open Graph tags
 */

import { useEffect } from 'react'

interface SEOProps {
  /**
   * Page title (will be appended with " | paiseh.sg")
   */
  title?: string

  /**
   * Page description for meta tags
   */
  description?: string

  /**
   * Keywords for this page
   */
  keywords?: string

  /**
   * Canonical URL for this page
   */
  canonicalUrl?: string

  /**
   * Open Graph image URL
   */
  ogImage?: string

  /**
   * Page type (website, article, profile, etc.)
   */
  ogType?: string

  /**
   * Whether this page should be indexed by search engines
   */
  noIndex?: boolean
}

export const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  noIndex = false,
}: SEOProps) => {
  useEffect(() => {
    // Update document title
    const fullTitle = title ? `${title} | Paiseh!` : 'Free Online Wishlist Maker Singapore | Create & Share Wishlists - Paiseh!'
    document.title = fullTitle

    // Update meta description
    const metaDescription = description || 'Create free wishlists online and share with family & friends. Best wishlist app in Singapore for birthdays, Christmas, weddings & baby showers. No duplicate gifts - our reservation system keeps surprises secret!'
    updateMetaTag('name', 'description', metaDescription)
    updateMetaTag('name', 'title', fullTitle)

    // Update keywords if provided
    if (keywords) {
      updateMetaTag('name', 'keywords', keywords)
    }

    // Update robots
    const robotsContent = noIndex ? 'noindex, nofollow' : 'index, follow'
    updateMetaTag('name', 'robots', robotsContent)

    // Update Open Graph tags
    updateMetaTag('property', 'og:title', fullTitle)
    updateMetaTag('property', 'og:description', metaDescription)
    updateMetaTag('property', 'og:type', ogType)

    if (canonicalUrl) {
      updateMetaTag('property', 'og:url', canonicalUrl)
      updateLinkTag('canonical', canonicalUrl)
    }

    if (ogImage) {
      updateMetaTag('property', 'og:image', ogImage)
      updateMetaTag('property', 'twitter:image', ogImage)
    }

    // Update Twitter Card tags
    updateMetaTag('property', 'twitter:title', fullTitle)
    updateMetaTag('property', 'twitter:description', metaDescription)

    if (canonicalUrl) {
      updateMetaTag('property', 'twitter:url', canonicalUrl)
    }
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, noIndex])

  return null
}

/**
 * Helper function to update or create a meta tag
 */
function updateMetaTag(attribute: string, attributeValue: string, content: string) {
  let element = document.querySelector(`meta[${attribute}="${attributeValue}"]`)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, attributeValue)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

/**
 * Helper function to update or create a link tag
 */
function updateLinkTag(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement

  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }

  element.setAttribute('href', href)
}
